import * as React from "react";
import { StreetviewData, ToTheDoorData } from "../types";
import { AcquireAddressStateProvider } from "./AcquireAddressStateProvider";

const AddressGeoPointContext = React.createContext<{
  addressGeoPoint: google.maps.LatLngLiteral | null;
  setAddressGeoPoint: React.Dispatch<
    React.SetStateAction<google.maps.LatLngLiteral | null>
  >;
} | null>(null);

const AddressStreetviewContext = React.createContext<{
  addressStreetviewData: StreetviewData | null;
  setAddressStreetviewData: React.Dispatch<
    React.SetStateAction<StreetviewData | null>
  >;
} | null>(null);

const AddressToTheDoorContext = React.createContext<{
  addressToTheDoorData: ToTheDoorData | null;
  setAddressToTheDoorData: React.Dispatch<
    React.SetStateAction<ToTheDoorData | null>
  >;
} | null>(null);

interface Props {
  children: React.ReactNode;
}

export function AddressStateProvider({ children }: Props) {
  const [geoPoint, setGeoPoint] =
    React.useState<google.maps.LatLngLiteral | null>(null);
  const [streetviewData, setStreetviewData] =
    React.useState<StreetviewData | null>(null);
  const [toTheDoorData, setToTheDoorData] =
    React.useState<ToTheDoorData | null>(null);
  const addressGeopointContext = React.useMemo(
    () => ({ addressGeoPoint: geoPoint, setAddressGeoPoint: setGeoPoint }),
    [geoPoint]
  );
  const addressStreetviewContext = React.useMemo(
    () => ({
      addressStreetviewData: streetviewData,
      setAddressStreetviewData: setStreetviewData,
    }),
    [streetviewData]
  );
  const addressToTheDoorContext = React.useMemo(
    () => ({
      addressToTheDoorData: toTheDoorData,
      setAddressToTheDoorData: setToTheDoorData,
    }),
    [toTheDoorData]
  );

  return (
    <AddressGeoPointContext.Provider value={addressGeopointContext}>
      <AddressStreetviewContext.Provider value={addressStreetviewContext}>
        <AddressToTheDoorContext.Provider value={addressToTheDoorContext}>
          <AcquireAddressStateProvider>{children}</AcquireAddressStateProvider>
        </AddressToTheDoorContext.Provider>
      </AddressStreetviewContext.Provider>
    </AddressGeoPointContext.Provider>
  );
}

export function useAddressGeopoint() {
  const context = React.useContext(AddressGeoPointContext);
  if (context == null) {
    throw new Error(
      "useAddressGeopoint must be used under AddressGeoPointContext"
    );
  }
  return context;
}

export function useAddressStreetviewData() {
  const context = React.useContext(AddressStreetviewContext);
  if (context == null) {
    throw new Error(
      "useAddressStreetviewData must be used under AddressStreetviewContext"
    );
  }
  return context;
}

export function useAddressToTheDoorData() {
  const context = React.useContext(AddressToTheDoorContext);
  if (context == null) {
    throw new Error(
      "useAddressToTheDoorData must be used under AddressToTheDoorContext"
    );
  }
  return context;
}
